<template>
  <div
    v-if="store.visible"
    :class="ns.b()"
    role="dialog"
    :aria-label="t('z.table.aiAssistant.title')"
  >
    <header :class="ns.e('header')">
      <div :class="ns.e('titles')">
        <h2 :class="ns.e('title')">{{ t('z.table.aiAssistant.title') }}</h2>
        <p :class="ns.e('subtitle')">{{ subtitle }}</p>
      </div>
      <div :class="ns.e('actions')">
        <z-tooltip :content="t('z.table.aiAssistant.newConversation')" placement="top">
          <z-button
            link
            :icon="icons.Plus"
            :aria-label="t('z.table.aiAssistant.newConversation')"
            @click="$grid.startAiConversation?.()"
          />
        </z-tooltip>
        <z-tooltip :content="t('z.table.aiAssistant.history')" placement="top">
          <z-button
            link
            :icon="icons.Stopwatch"
            :class="{ [ns.is('active')]: store.historyVisible }"
            :aria-label="t('z.table.aiAssistant.history')"
            @click="store.historyVisible = !store.historyVisible"
          />
        </z-tooltip>
        <z-tooltip :content="t('z.table.aiAssistant.close')" placement="top">
          <z-button
            link
            :icon="icons.Close"
            :aria-label="t('z.table.aiAssistant.close')"
            @click="$grid.closeAiAssistant?.()"
          />
        </z-tooltip>
      </div>
    </header>

    <div v-if="store.historyVisible" :class="ns.e('history')">
      <p v-if="!store.conversations.length" :class="ns.e('empty')">
        {{ t('z.table.aiAssistant.empty') }}
      </p>
      <button
        v-for="item in store.conversations"
        :key="item.id"
        type="button"
        :class="[ns.e('history-item'), { [ns.is('active')]: item.id === store.activeConversationId }]"
        @click="selectConversation(item.id)"
      >
        {{ item.title || t('z.table.aiAssistant.newConversation') }}
      </button>
    </div>

    <div v-else :class="ns.e('body')">
      <p v-if="!activeConversation?.prompts.length" :class="ns.e('empty')">
        {{ t('z.table.aiAssistant.empty') }}
      </p>
      <div
        v-for="prompt in activeConversation?.prompts"
        :key="prompt.id"
        :class="[ns.e('prompt'), ns.is(prompt.variant || 'success')]"
      >
        <div :class="ns.e('prompt-query')">{{ prompt.value }}</div>
        <div v-if="prompt.helperText || prompt.variant === 'processing'" :class="ns.e('prompt-reply')">
          {{
            prompt.variant === 'processing'
              ? t('z.table.aiAssistant.sending')
              : prompt.helperText
          }}
        </div>
      </div>
    </div>

    <form :class="ns.e('composer')" @submit.prevent="onSubmit">
      <z-input
        v-model="store.input"
        :placeholder="t('z.table.aiAssistant.placeholder')"
        :disabled="store.sending"
        :class="ns.e('input')"
        @keyup.enter="onSubmit"
      />
      <z-button
        link
        :icon="icons.Top"
        :disabled="store.sending || !store.input.trim()"
        native-type="submit"
        :aria-label="t('z.table.aiAssistant.send')"
      />
    </form>

    <div v-if="store.suggestions.length && !store.historyVisible" :class="ns.e('suggestions')">
      <span :class="ns.e('suggestions-label')">
        {{ t('z.table.aiAssistant.suggestions') }}
      </span>
      <div :class="ns.e('suggestion-list')">
        <button
          v-for="item in store.suggestions"
          :key="item"
          type="button"
          :class="ns.e('suggestion')"
          :disabled="store.sending"
          @click="onSuggestion(item)"
        >
          {{ item }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import ZButton from '@zoryn/components/button'
import ZInput from '@zoryn/components/input'
import ZTooltip from '@zoryn/components/tooltip'
import { Close, Plus, Stopwatch, Top } from '@zoryn/icons'
import { useLocale, useNamespace } from '@zoryn/hooks'
import { resolveIconComponent } from '@zoryn/utils'
import type { DataGrid } from '../types'

defineOptions({ name: 'GridAiPanel' })

const icons = {
  Plus: resolveIconComponent(Plus)!,
  Stopwatch: resolveIconComponent(Stopwatch)!,
  Close: resolveIconComponent(Close)!,
  Top: resolveIconComponent(Top)!,
}

const ns = useNamespace('grid-ai-panel')
const { t } = useLocale()
const $grid = inject('$grid') as DataGrid
const store = $grid.reactData.aiAssistantStore

const activeConversation = computed(() =>
  store.conversations.find((item) => item.id === store.activeConversationId)
)

const subtitle = computed(() => {
  if (store.historyVisible) {
    return t('z.table.aiAssistant.history')
  }
  const title = activeConversation.value?.title?.trim()
  return title || t('z.table.aiAssistant.newConversation')
})

function selectConversation(id: string) {
  store.activeConversationId = id
  store.historyVisible = false
}

function onSubmit() {
  void $grid.submitAiPrompt?.()
}

function onSuggestion(text: string) {
  void $grid.submitAiPrompt?.(text)
}
</script>
